import IconButton from '@components/v2/ui/IconButton/IconButton';
import Typography from '@components/v2/ui/Typography/Typography';
import { Box } from '@mui/material';
import _ from 'lodash';
import { ArrowLeft } from 'lucide-react';
import { useState } from 'react';
import LangEnum from '../../../../lang/LangEnum';
import { useTranslation } from '../../../../modules/translation/TranslationContext';
import { FilterPreferenceRecognizedLists } from '../../../../services/modules/userContextPreferences/userContextPreferences.types';
import { FilterProps, PossibleFilters } from '../../../Crud/filters/sharedBetweenFilters/types';
import DynamicOptionsSelectorV2 from '../../../FormInputs/MultiOptionsSelectorV2/DynamicOptionsSelectorV2';
import StaticOptionsSelectorV2 from '../../../FormInputs/MultiOptionsSelectorV2/StaticOptionsSelectorV2';
import { OPTION_WITH_SECONDARY_CLASS } from '../../../FormInputs/MultiOptionsSelectorV2/components/OptionsList/components/Option/Option';
import DateRangeFilterV2 from '../DateRangeFilterV2/DateRangeFilterV2';
import DynamicOptionsSelectorsCreatorV2 from '../DynamicOptionsSelectorsCreatorV2/DynamicOptionsSelectorsCreatorV2';
import FilterApplyFooter from '../FilterApplyFooter/FilterApplyFooter';
import NumberRangeFilterV2 from '../NumberRangeFilter/NumberRangeFilterV2';
import RadioFilterV2 from '../RadioFilterV2/RadioFilterV2';
import { getValueAndOnChange } from './utils/filterSelectedOptionList.utils';

interface FilterSelectedOptionListProps {
  onClickApply: () => void;
  option: FilterProps;
  list?: FilterPreferenceRecognizedLists;
  onClickBack?: () => void;
  canReturnToFilterList?: boolean;
  isDrawer?: boolean;
}

const FilterSelectedOptionList = ({
  onClickApply,
  option,
  list,
  onClickBack,
  canReturnToFilterList = true,
  isDrawer = false,
}: FilterSelectedOptionListProps) => {
  const { translate } = useTranslation();

  const initialState = getValueAndOnChange(option);
  const [internalState, setInternalState] = useState(initialState);
  const [hasError, setHasError] = useState(false);

  const handleSubmitFilter = () => {
    internalState.onChange(internalState.value);
    onClickApply();
  };

  const isDirty = !_.isEqual(internalState.value, initialState.value);

  const renderOption = () => {
    switch (option.type) {
      case PossibleFilters.OPTIONS_SELECTOR_WITH_DYNAMIC_DATA:
        return (
          <DynamicOptionsSelectorV2
            fullWidth
            fillHeight
            options={option.multiDynamicOptionsArgs.allOptions}
            isLoadingOptions={option.multiDynamicOptionsArgs.isLoading}
            loadMoreOptions={option.multiDynamicOptionsArgs.handleLoadMoreOptions}
            hideSearchField={false}
            value={internalState.value}
            onChange={(value) => setInternalState({ ...internalState, value })}
            canReorder={!isDirty}
          />
        );

      case PossibleFilters.OPTIONS_SELECTOR_CREATOR:
        return (
          <DynamicOptionsSelectorsCreatorV2
            list={list}
            option={option}
            onClickApply={onClickApply}
          />
        );

      case PossibleFilters.OPTIONS_SELECTOR_WITH_STATIC_DATA:
        return (
          <StaticOptionsSelectorV2
            fullWidth
            fillHeight
            options={option.multiStaticOptionsArgs.allOptions}
            value={internalState.value}
            onChange={(value) => setInternalState({ ...internalState, value })}
            canReorder={!isDirty}
          />
        );

      case PossibleFilters.DATE_RANGE:
        return (
          <DateRangeFilterV2
            initialDateLabel={option.dateRangeArgs.initialDateLabel}
            finalDateLabel={option.dateRangeArgs.finalDateLabel}
            value={internalState.value}
            minimized={false}
            isDrawer={isDrawer}
            onChange={(value) => setInternalState({ ...internalState, value })}
            onValidationError={setHasError}
          />
        );

      case PossibleFilters.NUMBER_RANGE:
      case PossibleFilters.NUMBER_RANGE_V2:
        return (
          <NumberRangeFilterV2
            type={option.numberRangeArgs.type}
            maxAvailableValue={option.numberRangeArgs.maxAvailableValue}
            minAvailableValue={option.numberRangeArgs.minAvailableValue}
            value={internalState.value}
            onChange={(value) => setInternalState({ ...internalState, value })}
            onValidationError={setHasError}
          />
        );

      case PossibleFilters.RADIO:
        return (
          <RadioFilterV2
            options={option.radioArgs.options}
            value={internalState.value}
            onChange={(value) => setInternalState({ ...internalState, value })}
          />
        );

      default:
        throw new Error('invalid type');
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: '1 1 auto',
        minWidth: 0,
        minHeight: 0,
      }}
    >
      {!isDrawer && !option.fixed && !option.fixedFilter && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            flexShrink: 0,
            paddingLeft: 1.5,
            marginBottom: 0.75,
          }}
        >
          {canReturnToFilterList && (
            <IconButton
              icon={ArrowLeft}
              onClick={onClickBack}
              ariaLabel={translate(LangEnum.GLOBAL_GO_BACK)}
            />
          )}
          <Typography variant="body1">{option.title}</Typography>
        </Box>
      )}
      <Box
        sx={{
          flex: '1 1 auto',
          minHeight: 0,
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
          paddingLeft: 1.25,
          '& .MuiListItemButton-root:has(.MuiCheckbox-root)': {
            paddingBlock: 0.25,
            ...(!isDrawer && {
              '& .MuiListItemText-root': { marginBlock: 0 },
            }),
          },
          [`& .MuiListItemButton-root.${OPTION_WITH_SECONDARY_CLASS}`]: {
            paddingBlock: 0.75,
          },
        }}
      >
        {renderOption()}
      </Box>
      {option.type !== PossibleFilters.OPTIONS_SELECTOR_CREATOR && (
        <FilterApplyFooter disabled={!isDirty || hasError} onClickApply={handleSubmitFilter} />
      )}
    </Box>
  );
};

export default FilterSelectedOptionList;
